import type { ApolloError } from '@apollo/client'
import type { FormikHelpers } from 'formik/dist/types'
import type { PartialDeep } from 'type-fest'
import type { Customer } from '@ztechapps/common.contract'
import type { MutationFunctionResult, MutationInput } from '@ztechapps/common.hook'
import type { MutationConfig } from '@ztechapps/common.utils'
import type { UseEditPersonInfoArgs } from '@ztechapps/webui.contract'
import type { FormValidateFunction } from '@ztechapps/webui.form'
import { useError, useMutation } from '@ztechapps/common.hook'
import { buildGraphqlQuery } from '@ztechapps/common.utils'
import { customerFragment } from '@ztechapps/customer.common'
import { CustomerFormValidate } from '../CustomerSelect/CustomerFormLayout'

const customerQuery = buildGraphqlQuery('Customer', customerFragment)

export type UseEditPersonInformationResult = {
  onEditPersonInfo: (args: UseEditPersonInfoArgs) => Promise<void>
  validate: FormValidateFunction<UseEditPersonInfoArgs>
  onRawEditPersonInfo: (args: UseEditPersonInfoArgs) => MutationFunctionResult<MutationInput>
  /** Resolves `true` after the update, `false` after a failure it already toasted and mapped onto its field. */
  onFormSubmit: (args: UseEditPersonInfoArgs, formikHelpers: FormikHelpers<UseEditPersonInfoArgs>) => Promise<boolean>
}

export const useEditPersonInformation = ({
  mutationConfig,
  afterUpdate,
}: {
  mutationConfig?: MutationConfig
  afterUpdate?: (args: UseEditPersonInfoArgs) => Promise<void>
} = {}): UseEditPersonInformationResult => {
  mutationConfig = mutationConfig ?? customerQuery.updateConfig
  const [onUpdate] = useMutation<Customer, PartialDeep<Customer>>({ ...mutationConfig })
  const { onError } = useError()

  const onEditPersonInfo = async (args: UseEditPersonInfoArgs) => {
    try {
      await onRawEditPersonInfo(args)
      await afterUpdate?.(args)
    } catch (e) {
      onError(e)
    }
  }

  const onRawEditPersonInfo = async (args: UseEditPersonInfoArgs) => {
    const { _id, company, profile, phone } = args
    const updateInput = {
      input: {
        _id,
        company: company.__isCreate ? { name: company?.name } : { _id: company?._id },
        profile: {
          name: profile?.name,
          email: profile?.email,
        },
        phone,
      },
    }

    const result = await onUpdate(updateInput)

    return result
  }

  const onFormSubmit = async (args: UseEditPersonInfoArgs, formikHelpers: FormikHelpers<UseEditPersonInfoArgs>) => {
    try {
      await onRawEditPersonInfo(args)
      return true
    } catch (e) {
      const apolloError = e as ApolloError
      const error = apolloError.graphQLErrors?.[0]?.extensions?.exception as any
      const code = error?.code

      if (code === 'VALIDATION_ERROR') {
        const validationError = error?.validationErrors?.[0]
        const field = validationError?.field
        const message = validationError?.message

        //we need to set the formik set error here
        formikHelpers.setFieldError(field, message)
      }
      //we also add the notification message
      onError(e)
      return false
    }
  }

  return {
    onEditPersonInfo,
    validate: CustomerFormValidate,
    onRawEditPersonInfo,
    onFormSubmit,
  }
}
